import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';

import { Button, Grid, Box, CircularProgress, Chip, Tabs, Tab, Stack, Typography, Tooltip } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { apiString as API_URL } from '../../../utils/constants';
import { copyObject } from '../../../utils/common';

import {
    getPastPresentHistory,
    getPastSurgicalHistory,
    getPhysicalExam,
    getReviewSystems,
    gotoNextMenu,
    gotoPreviousMenu,
    saveAssessmentWorkflow,
    setAssessmentType,
    setIsChanged,
    setMemberAssessment,
    setPastPresentHistory,
    setPastSurgicalHistory,
    setPhysicalExam,
    setReviewSystems,
} from '../../../store/assessment.slice';
import { RootState } from '../../../reducers';
import { AssessmentTypeEnum } from '../../../Enum/AssessmentTypeEnum';
import { AssessmentCategory } from '../../../Models/Assessment/AssessmentCategory.model';
import { setShowMessage } from '../../../store/shared.slice';
import { saveEngagementDraft } from '../../../store/documentEngagement.slice';
import { SelectionType } from '../../../Enum/SelectionTypeEnum';
import { formatDate } from '../../../utils/dateHelpers';
import { assessmentStatusEnum } from '../../../utils/assessments';
import AssessmentCategoryList from './PhysicalHealthPages/AssessmentCategoryList';
import CategoryQuestions from './PhysicalHealthPages/CategoryQuestions';
import { useAppDispatch } from '../../../store/store';
import MultiAssessmentDialog from './PhysicalHealthPages/MultiAssessmentDialog';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Physical_Health.pdf';
import { fetchTobaccoAndDrugAssessmentByMemberId, setTobaccoAndDrugAssessment, syncPPHTobaccoToTobaccoAndDrug } from '../../../store/hra.slice';

// ---------------------------------------------------------------------------
// Lookup maps — eliminates repetitive switch statements
// ---------------------------------------------------------------------------

/** Maps each assessment type to its corresponding Redux state key */
const ASSESSMENT_STATE_KEY: Partial<Record<AssessmentTypeEnum, 'pastPresentHistory' | 'pastSurgicalHistory' | 'reviewSystems' | 'physicalExam'>> = {
    [AssessmentTypeEnum.PastPresentHistory]: 'pastPresentHistory',
    [AssessmentTypeEnum.PastSurgicalHistory]: 'pastSurgicalHistory',
    [AssessmentTypeEnum.ReviewSystems]: 'reviewSystems',
    [AssessmentTypeEnum.PhysicalExam]: 'physicalExam',
};

/** Maps each assessment type to its engagement draft ID field */
const ENGAGEMENT_DRAFT_ID_FIELD: Partial<Record<AssessmentTypeEnum, string>> = {
    [AssessmentTypeEnum.PastPresentHistory]: 'PastPresentHistoryId',
    [AssessmentTypeEnum.PastSurgicalHistory]: 'PastSurgicalHistoryId',
    [AssessmentTypeEnum.ReviewSystems]: 'ReviewSystemId',
    [AssessmentTypeEnum.PhysicalExam]: 'PhysicalExamId',
};

/** Maps each assessment type to its display name */
const ASSESSMENT_DISPLAY_NAME: Partial<Record<AssessmentTypeEnum, string>> = {
    [AssessmentTypeEnum.PastPresentHistory]: 'Past Present History',
    [AssessmentTypeEnum.PastSurgicalHistory]: 'Past Surgical History',
    [AssessmentTypeEnum.ReviewSystems]: 'Review of Systems',
    [AssessmentTypeEnum.PhysicalExam]: 'Physical Exam',
};

// ---------------------------------------------------------------------------
// Cross-assessment sync configuration
// ---------------------------------------------------------------------------

type SyncMapping =
    | {
          /** Source: question > Yes-option > subOption is selected → set targetOption on target question */
          kind: 'subOptionToQuestion';
          sourceQuestion: string;
          sourceSubOption: string;
          targetQuestion: string;
          targetOption: string;
      }
    | {
          /** Source: mirror IsSelected on matching options (and their SubOptions) by name */
          kind: 'questionOptions';
          sourceQuestion: string;
      };

interface SyncConfig {
    categoryName: string;
    sourceType: AssessmentTypeEnum;
    targetType: AssessmentTypeEnum;
    mappings: SyncMapping[];
}

/**
 * Declarative table of all cross-assessment field syncs.
 * To add a new sync, just append an entry here — no logic changes needed.
 */
const SYNC_CONFIGS: SyncConfig[] = [
    {
        categoryName: 'Cardiovascular',
        sourceType: AssessmentTypeEnum.PastPresentHistory,
        targetType: AssessmentTypeEnum.PastSurgicalHistory,
        mappings: [
            { kind: 'subOptionToQuestion', sourceQuestion: 'ASHD/CAD', sourceSubOption: 'Bypass', targetQuestion: 'CABG', targetOption: 'Yes' },
            { kind: 'subOptionToQuestion', sourceQuestion: 'ASHD/CAD', sourceSubOption: 'Stent', targetQuestion: 'PTCA/Stent', targetOption: 'Yes' },
            { kind: 'subOptionToQuestion', sourceQuestion: 'CHF', sourceSubOption: 'Pacemaker', targetQuestion: 'Pacemaker', targetOption: 'Yes' },
            { kind: 'subOptionToQuestion', sourceQuestion: 'CHF', sourceSubOption: 'AICD', targetQuestion: 'AICD', targetOption: 'Yes' },
        ],
    },
    {
        categoryName: 'Pulmonary',
        sourceType: AssessmentTypeEnum.ReviewSystems,
        targetType: AssessmentTypeEnum.PhysicalExam,
        mappings: [{ kind: 'questionOptions', sourceQuestion: 'Cough' }],
    },
    {
        categoryName: 'Pulmonary',
        sourceType: AssessmentTypeEnum.PhysicalExam,
        targetType: AssessmentTypeEnum.ReviewSystems,
        mappings: [{ kind: 'questionOptions', sourceQuestion: 'Cough' }],
    },
];

type AssessmentWorkflowSaveResponse = {
    MemberAssessment?: any;
    SyncedAssessments?: any[];
    SyncedTobaccoAndDrugAssessment?: any;
};

// ---------------------------------------------------------------------------

const PhysicalHealth: React.FC<{ memberId: string }> = React.memo(({ memberId }) => {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const hasCriticalNotes = store.getState().memberDemographicsDetails.hasCriticalNotes;
    const [stickyTop, setStickyTop] = useState(128);

    React.useLayoutEffect(() => {
        const updateHeights = () => {
            const header = document.getElementById('member-header')?.offsetHeight || 64;
            const criticalNotes = hasCriticalNotes ? 32 : 0;
            setStickyTop(header + criticalNotes);
        };

        updateHeights();
        window.addEventListener('resize', updateHeights);
        return () => window.removeEventListener('resize', updateHeights);
    }, []);
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const [isPageLoaded, setIsPageLoaded] = useState(false);

    // LOCAL STATE: Store all 4 assessments here to avoid Redux overhead on tab changes
    const [localAssessments, setLocalAssessments] = useState<Partial<Record<AssessmentTypeEnum, any>>>({
        [AssessmentTypeEnum.PastPresentHistory]: null,
        [AssessmentTypeEnum.PastSurgicalHistory]: null,
        [AssessmentTypeEnum.ReviewSystems]: null,
        [AssessmentTypeEnum.PhysicalExam]: null,
    });

    // Track which assessments have unsaved changes
    const [changedAssessments, setChangedAssessments] = useState<Set<AssessmentTypeEnum>>(new Set());

    // Track which assessments have been modified in this session (persistent across auto-saves)
    const [modifiedAssessments, setModifiedAssessments] = useState<Set<AssessmentTypeEnum>>(new Set());

    // Auto-save configuration
    const AUTO_SAVE_ENABLED = true;
    const DEBOUNCE_DELAY = 10000; // 10 seconds after last change
    const autoSaveTimerRef = React.useRef<NodeJS.Timeout | null>(null);
    const localAssessmentsRef = React.useRef(localAssessments);
    const tabsRef = React.useRef<HTMLDivElement>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);

    // Get data from Redux
    const { assessmentType, isChanged, isSaving, pastPresentHistory, pastSurgicalHistory, reviewSystems, physicalExam, isFetching } = useSelector(
        (state: RootState) => state.assessment
    );
    const { isLoading } = useSelector((state: RootState) => state.shared);
    const { engagementDraft, memberAssessmentHistory } = useSelector((state: RootState) => state.documentEngagement);

    // Update ref whenever localAssessments changes
    useEffect(() => {
        localAssessmentsRef.current = localAssessments;
    }, [localAssessments]);

    // Store expanded category per tab - only one category can be expanded per tab
    const [expandedCategoryByTab, setExpandedCategoryByTab] = useState<Partial<Record<AssessmentTypeEnum, string | null>>>({
        [AssessmentTypeEnum.PastPresentHistory]: null,
        [AssessmentTypeEnum.PastSurgicalHistory]: null,
        [AssessmentTypeEnum.ReviewSystems]: null,
        [AssessmentTypeEnum.PhysicalExam]: null,
    });

    // Ref to track if we just performed a save, to prevent overwriting local state with the response
    const isInternalSaveUpdate = React.useRef(false);

    // Sync Redux data to local state when it loads
    useEffect(() => {
        // If this update was triggered by our own auto-save response, do not overwrite local state
        // This prevents the "typing while saving" bug where the server response (missing new keystrokes) clobbers user input
        // NOTE: We check the flag but do NOT immediately clear it, because auto-save triggers multiple Redux updates
        // (optimistic update, API response, engagement save). The flag is cleared in performAutoSave after all updates complete.
        if (isInternalSaveUpdate.current) {
            return;
        }

        const updates: Partial<Record<AssessmentTypeEnum, any>> = {};
        if (pastPresentHistory?.Assessment) updates[AssessmentTypeEnum.PastPresentHistory] = pastPresentHistory;
        if (pastSurgicalHistory?.Assessment) updates[AssessmentTypeEnum.PastSurgicalHistory] = pastSurgicalHistory;
        if (reviewSystems?.Assessment) updates[AssessmentTypeEnum.ReviewSystems] = reviewSystems;
        if (physicalExam?.Assessment) updates[AssessmentTypeEnum.PhysicalExam] = physicalExam;

        if (Object.keys(updates).length > 0) {
            setLocalAssessments((prev) => ({ ...prev, ...updates }));
        }
    }, [pastPresentHistory, pastSurgicalHistory, reviewSystems, physicalExam]);

    const pastPresentHistoryMenu = localAssessmentsRef.current[AssessmentTypeEnum.PastPresentHistory]?.Status;
    const pastSurgicalHistoryMenu = localAssessmentsRef.current[AssessmentTypeEnum.PastSurgicalHistory]?.Status;
    const reviewSystemsMenu = localAssessmentsRef.current[AssessmentTypeEnum.ReviewSystems]?.Status;
    const physicalExamMenu = localAssessmentsRef.current[AssessmentTypeEnum.PhysicalExam]?.Status;

    /**
     * Applies a single SyncConfig against the current localAssessmentsRef snapshots.
     * Returns the updated target assessment object, or null if nothing changed.
     */
    const applySyncConfig = useCallback((config: SyncConfig): any | null => {
        const sourceAssessment = localAssessmentsRef.current[config.sourceType];
        const targetAssessment = localAssessmentsRef.current[config.targetType];

        const sourceCategory = sourceAssessment?.Assessment?.Categories?.find((c: any) => c.Name === config.categoryName);
        const targetCategory = targetAssessment?.Assessment?.Categories?.find((c: any) => c.Name === config.categoryName);

        if (!sourceCategory || !targetCategory) return null;

        let hasChanges = false;
        const updatedTarget = copyObject(targetAssessment);
        const updatedTargetCategory = updatedTarget?.Assessment?.Categories?.find((c: any) => c.Name === config.categoryName);
        if (!updatedTargetCategory) {
            return null;
        }

        for (const mapping of config.mappings) {
            if (mapping.kind === 'subOptionToQuestion') {
                const srcQ = sourceCategory.Questions?.find((q: any) => q.Description === mapping.sourceQuestion);
                const srcYes =
                    config.sourceType === AssessmentTypeEnum.PastPresentHistory
                        ? srcQ?.Options?.find((o: any) => (o.Name === 'Yes - Past' || o.Name === 'Yes - Present') && o.IsSelected)
                        : srcQ?.Options?.find((o: any) => o.Name === 'Yes' && o.IsSelected);
                const srcSub = srcYes?.SubOptions?.find((s: any) => s.Name === mapping.sourceSubOption && s.IsSelected);
                if (!srcSub) continue;

                const tgtQ = updatedTargetCategory.Questions?.find((q: any) => q.Description === mapping.targetQuestion);
                if (!tgtQ?.Options) continue;
                const alreadySelected = tgtQ.Options.find((o: any) => o.Name === mapping.targetOption)?.IsSelected;
                if (!alreadySelected) {
                    tgtQ.Options.forEach((o: any) => {
                        o.IsSelected = o.Name === mapping.targetOption;
                    });
                    hasChanges = true;
                }
            } else {
                const srcQ = sourceCategory.Questions?.find((q: any) => q.Description === mapping.sourceQuestion);
                const tgtQ = updatedTargetCategory.Questions?.find((q: any) => q.Description === mapping.sourceQuestion);
                if (!srcQ || !tgtQ) continue;

                srcQ.Options?.forEach((srcOpt: any) => {
                    const tgtOpt = tgtQ.Options?.find((o: any) => o.Name === srcOpt.Name);
                    if (!tgtOpt) return;
                    if (tgtOpt.IsSelected !== srcOpt.IsSelected) {
                        tgtOpt.IsSelected = srcOpt.IsSelected;
                        hasChanges = true;
                    }
                    srcOpt.SubOptions?.forEach((srcSub: any) => {
                        const tgtSub = tgtOpt.SubOptions?.find((s: any) => s.Name === srcSub.Name);
                        if (tgtSub && tgtSub.IsSelected !== srcSub.IsSelected) {
                            tgtSub.IsSelected = srcSub.IsSelected;
                            hasChanges = true;
                        }
                    });
                });
            }
        }

        return hasChanges ? updatedTarget : null;
    }, []);

    // Auto-save function - optimized to make a single API call
    const performAutoSave = useCallback(
        async (assessmentTypeToSave: AssessmentTypeEnum) => {
            // Skip auto-save if no changes have been made since last save
            const currentState = store.getState().assessment;
            if (!currentState.isChanged) {
                return;
            }

            // Use ref to get the latest state
            const assessment = localAssessmentsRef.current[assessmentTypeToSave];
            if (!assessment?.Assessment?.Categories) {
                return;
            }

            // Capture current Redux state for rollback
            const stateKey = ASSESSMENT_STATE_KEY[assessmentTypeToSave];
            const previousAssessmentData = stateKey !== undefined ? currentState[stateKey] ?? null : null;

            try {
                dispatch(setShowMessage(true, 'Saving...', 'info'));

                // Set flag to ignore the next Redux update in the useEffect sync
                isInternalSaveUpdate.current = true;

                // Sync the local state to Redux (Optimistic Update)
                dispatch(setMemberAssessment(assessment));

                // Make a single API call to save the entire assessment
                const response = await axios.put(`${API_URL}/assessment/createorupdateassessmentworkflow`, assessment);
                const workflowResult = response.data as AssessmentWorkflowSaveResponse;
                const savedAssessment = workflowResult?.MemberAssessment ?? response.data;

                // Update Redux with the response (server IDs, etc.)
                // The useEffect will skip syncing this back to localAssessments because of isInternalSaveUpdate flag
                dispatch(setMemberAssessment(savedAssessment));

                // Ensure the saved source assessment (with generated/updated Id) is persisted in local tab state
                // so subsequent saves update the same MemberAssessmentLite instead of creating a new one.
                setLocalAssessments((prev) => ({
                    ...prev,
                    [assessmentTypeToSave]: savedAssessment,
                }));

                switch (assessmentTypeToSave) {
                    case AssessmentTypeEnum.PastPresentHistory:
                        dispatch(setPastPresentHistory(savedAssessment));
                        break;
                    case AssessmentTypeEnum.PastSurgicalHistory:
                        dispatch(setPastSurgicalHistory(savedAssessment));
                        break;
                    case AssessmentTypeEnum.ReviewSystems:
                        dispatch(setReviewSystems(savedAssessment));
                        break;
                    case AssessmentTypeEnum.PhysicalExam:
                        dispatch(setPhysicalExam(savedAssessment));
                        break;
                    default:
                        break;
                }

                if (Array.isArray(workflowResult?.SyncedAssessments)) {
                    const syncedByType: Partial<Record<AssessmentTypeEnum, any>> = {};

                    workflowResult.SyncedAssessments.forEach((syncedAssessment) => {
                        const syncedType = syncedAssessment?.Assessment?.AssessmentType as AssessmentTypeEnum | undefined;
                        if (syncedType === undefined) return;

                        syncedByType[syncedType] = syncedAssessment;

                        switch (syncedType) {
                            case AssessmentTypeEnum.PastPresentHistory:
                                dispatch(setPastPresentHistory(syncedAssessment));
                                break;
                            case AssessmentTypeEnum.PastSurgicalHistory:
                                dispatch(setPastSurgicalHistory(syncedAssessment));
                                break;
                            case AssessmentTypeEnum.ReviewSystems:
                                dispatch(setReviewSystems(syncedAssessment));
                                break;
                            case AssessmentTypeEnum.PhysicalExam:
                                dispatch(setPhysicalExam(syncedAssessment));
                                break;
                            default:
                                break;
                        }
                    });

                    if (Object.keys(syncedByType).length > 0) {
                        setLocalAssessments((prev) => ({ ...prev, ...syncedByType }));
                    }
                }

                if (workflowResult?.SyncedTobaccoAndDrugAssessment) {
                    dispatch(setTobaccoAndDrugAssessment(workflowResult.SyncedTobaccoAndDrugAssessment));
                }

                // Update the engagement draft with the assessment ID
                const idField = ENGAGEMENT_DRAFT_ID_FIELD[assessmentTypeToSave];
                if (idField === undefined) {
                    dispatch(setShowMessage(true, 'Failed to save changes: Invalid assessment type', 'error'));
                    throw new Error('Invalid assessment type for engagement draft ID field');
                }
                const updatedEngagementDraft = { ...copyObject(engagementDraft), [idField]: savedAssessment.Id };

                // Save the engagement draft (dispatch returns a promise)
                const updatedMemberAssessmentHistory = { ...memberAssessmentHistory, EngagementDraft: updatedEngagementDraft };
                await dispatch(saveEngagementDraft(updatedMemberAssessmentHistory, false, () => {}));

                dispatch(setShowMessage(true, 'All changes saved', 'success'));

                // Only reset isChanged if the user hasn't made NEW changes during the save
                // Since this runs after await, check current store state vs saved state is complex
                // For now, simpler compromise: reset it.
                // Note: If user typed during save, isChanged might need to remain true.
                // Ideally, we'd check a 'lastModified' timestamp.
                dispatch(setIsChanged(false));

                // Clear this assessment from the changed set
                setChangedAssessments((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(assessmentTypeToSave);
                    return newSet;
                });

                // Clear the isInternalSaveUpdate flag after a short delay to allow all Redux updates to settle
                // This prevents the sync effect from overwriting local state during multi-step save operations
                setTimeout(() => {
                    isInternalSaveUpdate.current = false;
                }, 100);
            } catch (error) {
                console.error('Auto-save failed:', error);

                // Rollback Redux state
                if (previousAssessmentData) {
                    isInternalSaveUpdate.current = true; // Rollback should also not clobber local state if user is typing...
                    // Actually, on error, we PROBABLY want to revert local state to previous good state to avoid desync
                    // So we do NOT set isInternalSaveUpdate to true here, allowing the useEffect to revert local changes?
                    // No, rollback means "server save failed". Local changes might still be valid.
                    // Keeping local revisions is usually safer.
                    dispatch(setMemberAssessment(previousAssessmentData));

                    // Clear flag after rollback settles
                    setTimeout(() => {
                        isInternalSaveUpdate.current = false;
                    }, 100);
                }

                dispatch(setShowMessage(true, 'Failed to save changes', 'error'));
            }
        },
        [dispatch, engagementDraft]
    );

    // Scroll to accordion function
    const scrollToAccordion = useCallback(
        (categoryName: string | null, delay: number = 100) => {
            // Use setTimeout + rAF to ensure DOM has updated after tab change
            setTimeout(() => {
                requestAnimationFrame(() => {
                    const tabsHeight = tabsRef.current?.offsetHeight || 48;
                    const stickyOffset = stickyTop + tabsHeight;

                    if (categoryName) {
                        const accordionElement = document.getElementById(`${categoryName}-header`);
                        if (accordionElement) {
                            const rect = accordionElement.getBoundingClientRect();
                            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                            // Scroll to show the accordion header, accounting for sticky navbars + tabs + buffer
                            const targetPosition = rect.top + scrollTop - stickyOffset - 32; // 32px buffer

                            window.scrollTo({
                                top: Math.max(0, targetPosition),
                                behavior: 'smooth',
                            });
                        }
                    } else {
                        // Scroll to header if no category specified
                        if (headerRef.current) {
                            const element = headerRef.current;
                            const elementRect = element.getBoundingClientRect();
                            const absoluteElementTop = elementRect.top + window.pageYOffset;
                            // Scroll to show the header, accounting for sticky navbars (header is above tabs)
                            const targetPosition = absoluteElementTop - stickyTop - 32; // 32px buffer

                            window.scrollTo({
                                top: Math.max(0, targetPosition),
                                behavior: 'smooth',
                            });
                        } else {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                    }
                });
            }, delay); // Small delay to ensure DOM is updated
        },
        [stickyTop]
    );

    const handleTabChange = useCallback(
        (event: React.ChangeEvent<{}>, newValue: AssessmentTypeEnum) => {
            const currentExpandedCategory = expandedCategoryByTab[assessmentType];

            // Switch immediately for responsiveness
            updateTab(newValue, currentExpandedCategory);

            // Save in background if there are unsaved changes
            if (AUTO_SAVE_ENABLED && isChanged) {
                if (autoSaveTimerRef.current) {
                    clearTimeout(autoSaveTimerRef.current);
                }
                setTimeout(() => {
                    void performAutoSave(assessmentType);
                }, 0);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [assessmentType, dispatch, expandedCategoryByTab, scrollToAccordion, isChanged, performAutoSave]
    );

    const updateTab = useCallback(
        (newValue: AssessmentTypeEnum, currentExpandedCategory: string | null) => {
            // If there's a currently expanded category, check if it exists in the new tab
            if (currentExpandedCategory) {
                const newAssessment = localAssessments[newValue];
                const categoryExists = newAssessment?.Assessment?.Categories?.some((cat: any) => cat.Name === currentExpandedCategory);

                if (categoryExists) {
                    setExpandedCategoryByTab((prev) => ({
                        ...prev,
                        [newValue]: currentExpandedCategory,
                    }));

                    // Scroll to the accordion after tab change
                    scrollToAccordion(currentExpandedCategory);
                } else {
                    // Category doesn't exist in new tab, collapse all
                    setExpandedCategoryByTab((prev) => ({
                        ...prev,
                        [newValue]: null,
                    }));

                    // Scroll to header
                    scrollToAccordion(null);
                }
            } else {
                // No category expanded, scroll to header
                scrollToAccordion(null);
            }

            // ONLY update the tab indicator in Redux - NO setMemberAssessment dispatch
            dispatch(setAssessmentType(newValue));
        },
        [dispatch, scrollToAccordion, localAssessments]
    );

    const handleCategoryToggle = useCallback(
        (categoryName: string) => {
            const currentExpanded = expandedCategoryByTab[assessmentType];
            const newExpanded = currentExpanded === categoryName ? null : categoryName;

            setExpandedCategoryByTab((prev) => ({
                ...prev,
                [assessmentType]: newExpanded,
            }));

            // Scroll to the category when expanding (longer delay to ensure accordion content is fully rendered)
            if (newExpanded) {
                scrollToAccordion(newExpanded, 800);
            }
        },
        [assessmentType, expandedCategoryByTab, scrollToAccordion]
    );

    const handleCollapseAll = useCallback(() => {
        setExpandedCategoryByTab((prev) => ({
            ...prev,
            [assessmentType]: null,
        }));
    }, [assessmentType]);

    const handleExpandAll = useCallback(() => {
        // Expand all accordions by setting the expanded category to all categories
        setExpandedCategoryByTab((prev) => ({
            ...prev,
            [assessmentType]: 'All',
        }));
    }, [assessmentType]);

    // Get master radio options for a category based on assessment type and available options
    const getMasterRadioOptions = useCallback(
        (category: AssessmentCategory) => {
            // Add defensive checks
            if (!category || !category.Questions) return [];

            // PPH uses split Yes options
            if (assessmentType === AssessmentTypeEnum.PastPresentHistory) {
                const hasPPHOptions = category.Questions.some(
                    (q) => q.Options && q.Options.some((o) => !o.IsArchived && ['Yes - Past', 'Yes - Present', 'No', 'Not Assessed'].includes(o.Name))
                );
                if (!hasPPHOptions) return [];
                return [
                    { value: 'Yes - Past', label: 'Yes - Past' },
                    { value: 'Yes - Present', label: 'Yes - Present' },
                    { value: 'No', label: 'No' },
                    { value: 'Not Assessed', label: 'Not Assessed' },
                ];
            }

            // Check if any question in the category has Yes, No, or Not Assessed options
            const hasStandardOptions = category.Questions.some((q) => q.Options && q.Options.some((o) => ['Yes', 'No', 'Not Assessed'].includes(o.Name)));

            if (!hasStandardOptions) {
                return [];
            }
            return [
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
                { value: 'Not Assessed', label: 'Not Assessed' },
            ];
        },
        [assessmentType]
    );

    // Get the current master selection for a category (if all questions have the same selection)
    // Memoized per category ID to avoid expensive recomputation on every render
    const getCurrentMasterSelection = useCallback((category: AssessmentCategory) => {
        // Add defensive checks
        if (!category || !category.Questions || category.Questions.length === 0) return null;

        // Get all questions that have selectable options
        const selectableQuestions = category.Questions.filter(
            (q) => (q.SelectionType === SelectionType.SingleSelect || q.SelectionType === SelectionType.MultiSelect) && q.Options && q.Options.length > 0
        );

        if (selectableQuestions.length === 0) return null;

        // Check if all questions have the same selected option
        let commonSelection: string | null | undefined = undefined;
        for (const question of selectableQuestions) {
            if (!question.Options) continue; // Skip questions without options

            const selectedOption = question.Options.find((opt) => opt.IsSelected);
            const currentSelection = selectedOption ? selectedOption.Name : null;

            if (commonSelection === undefined) {
                commonSelection = currentSelection;
            } else if (commonSelection !== currentSelection) {
                // Questions have different selections, so no common master selection
                return null;
            }
        }

        return commonSelection ?? null;
    }, []);

    // Memoize master selection result per category to avoid recalculation
    const masterSelectionCache = React.useMemo(() => {
        const cache = new Map<string, string | null>();
        const currentAssessment = localAssessments[assessmentType];
        if (currentAssessment?.Assessment?.Categories) {
            currentAssessment.Assessment.Categories.forEach((cat: AssessmentCategory) => {
                cache.set(cat.Id, getCurrentMasterSelection(cat));
            });
        }
        return cache;
    }, [localAssessments[assessmentType]?.Assessment?.Categories, assessmentType, getCurrentMasterSelection]);

    const getCurrentMasterSelectionCached = useCallback(
        (category: AssessmentCategory) => {
            if (!category) return null;
            return masterSelectionCache.get(category.Id) ?? null;
        },
        [masterSelectionCache]
    );

    const renderCategoryQuestions = useCallback(
        ({ category, assessmentType, memberId, onChange }) => (
            <CategoryQuestions assessmentType={assessmentType} category={category} onChange={onChange} memberId={memberId} />
        ),
        []
    );

    // Helper to update local state and trigger auto-save
    const updateLocalStateAndSave = useCallback(
        (updatedCategory: AssessmentCategory) => {
            const syncResults: { type: AssessmentTypeEnum; assessment: any }[] = SYNC_CONFIGS.filter(
                (cfg) => cfg.sourceType === assessmentType && cfg.categoryName === updatedCategory.Name
            ).flatMap((cfg) => {
                const synced = applySyncConfig(cfg);
                return synced ? [{ type: cfg.targetType, assessment: synced }] : [];
            });

            if (assessmentType === AssessmentTypeEnum.PastPresentHistory && updatedCategory.Name === 'Social History') {
                const tobaccoQuestion = updatedCategory.Questions?.find((q) => q.Description === 'Tobacco Use');
                if (tobaccoQuestion) {
                    const yesPastOption = tobaccoQuestion.Options?.find((opt) => opt.Name === 'Yes - Past');
                    const yesPresentOption = tobaccoQuestion.Options?.find((opt) => opt.Name === 'Yes - Present');
                    const noOption = tobaccoQuestion.Options?.find((opt) => opt.Name === 'No');

                    let hasUse: boolean | null = null;
                    if (yesPastOption?.IsSelected || yesPresentOption?.IsSelected) hasUse = true;
                    else if (noOption?.IsSelected) hasUse = false;

                    const selectedYesOption = yesPastOption?.IsSelected ? yesPastOption : yesPresentOption?.IsSelected ? yesPresentOption : null;
                    const yearsSubQuestion = selectedYesOption?.SubQuestions?.find((sq) => sq.Description === '# of years');
                    let yearsOfUse: number | null = null;
                    if (yearsSubQuestion && yearsSubQuestion.AnswerText !== undefined) {
                        const raw = yearsSubQuestion.AnswerText?.trim();
                        if (raw !== '') {
                            const parsedYears = parseInt(raw, 10);
                            yearsOfUse = Number.isNaN(parsedYears) ? null : parsedYears;
                        }
                    }

                    dispatch(syncPPHTobaccoToTobaccoAndDrug(hasUse, yearsOfUse));
                }
            }

            setLocalAssessments((prev) => {
                const currentAssessment = prev[assessmentType];
                if (!currentAssessment?.Assessment?.Categories) return prev;

                const updatedState: Partial<Record<AssessmentTypeEnum, any>> = {
                    ...prev,
                    [assessmentType]: {
                        ...currentAssessment,
                        Assessment: {
                            ...currentAssessment.Assessment,
                            Categories: currentAssessment.Assessment.Categories.map((cat: AssessmentCategory) =>
                                cat.Id === updatedCategory.Id ? updatedCategory : cat
                            ),
                        },
                    },
                };

                for (const result of syncResults) {
                    updatedState[result.type] = result.assessment;
                }

                return updatedState;
            });

            const allChangedTypes = [assessmentType, ...syncResults.map((r) => r.type)];
            setChangedAssessments((prev) => {
                const s = new Set(prev);
                allChangedTypes.forEach((t) => s.add(t));
                return s;
            });
            setModifiedAssessments((prev) => {
                const s = new Set(prev);
                allChangedTypes.forEach((t) => s.add(t));
                return s;
            });
            dispatch(setIsChanged(true));

            if (AUTO_SAVE_ENABLED) {
                if (autoSaveTimerRef.current) {
                    clearTimeout(autoSaveTimerRef.current);
                }
                const currentAssessmentType = assessmentType;
                autoSaveTimerRef.current = setTimeout(() => {
                    performAutoSave(currentAssessmentType);
                }, DEBOUNCE_DELAY);
            }
        },
        [assessmentType, dispatch, performAutoSave, AUTO_SAVE_ENABLED, applySyncConfig]
    );

    // Handle master radio button change for a specific category
    const handleMasterRadioChange = useCallback(
        (category: AssessmentCategory, selectedValue: string, event: React.MouseEvent) => {
            // Stop event propagation to prevent accordion from toggling
            event.stopPropagation();

            // Add defensive checks
            if (!category || !category.Questions) {
                return;
            }

            const updatedQuestions =
                category.Questions?.map((question) => {
                    if (question.SelectionType === SelectionType.SingleSelect || question.SelectionType === SelectionType.MultiSelect) {
                        const targetOption = question.Options?.find((option) => option.Name === selectedValue);
                        if (targetOption) {
                            return {
                                ...question,
                                Options: question.Options.map((option) => ({
                                    ...option,
                                    IsSelected: option.Name === selectedValue,
                                })),
                            };
                        }
                    }
                    return question;
                }) || [];

            const updatedCategory = { ...category, Questions: updatedQuestions };
            updateLocalStateAndSave(updatedCategory);
        },
        [updateLocalStateAndSave]
    );

    // Handle category change from individual questions (with auto-save)
    const handleOnChangeCategory = useCallback(
        (updatedCategory: AssessmentCategory) => {
            // Add defensive check
            if (!updatedCategory) {
                return;
            }

            // Check if category actually changed to avoid unnecessary updates
            const currentAssessment = localAssessments[assessmentType];
            if (currentAssessment?.Assessment?.Categories) {
                const existingCategory = currentAssessment.Assessment.Categories.find((cat: AssessmentCategory) => cat.Id === updatedCategory.Id);
                // Skip update only if data hasn't changed (reference check for performance;
                // relies on structural sharing where unchanged categories keep their references)
                if (existingCategory === updatedCategory) {
                    return;
                }
            }

            updateLocalStateAndSave(updatedCategory);
        },
        [updateLocalStateAndSave, assessmentType, localAssessments]
    );

    const handleOnClickSave = useCallback(async () => {
        // Clear the auto-save timer when dialog opens to prevent state changes during dialog interaction
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
            autoSaveTimerRef.current = null;
        }
        const assessmentTypes = [
            AssessmentTypeEnum.PastPresentHistory,
            AssessmentTypeEnum.PastSurgicalHistory,
            AssessmentTypeEnum.ReviewSystems,
            AssessmentTypeEnum.PhysicalExam,
        ];

        // Process each assessment: save changes first (if any), then apply the action
        for (const type of assessmentTypes) {
            const assessment = localAssessments[type];
            const hasChanges = changedAssessments.has(type);

            if (!assessment?.Assessment?.Categories?.[0]) {
                continue; // Skip if assessment data is not available
            }
            if (hasChanges) {
                dispatch(setShowMessage(true, 'Saving changes...', 'info'));
                dispatch(setMemberAssessment(assessment));
                await dispatch(saveAssessmentWorkflow(assessment.Assessment.Categories[0], false, false));
            }
        }
        setShowSaveAlert(true);
    }, [localAssessments, changedAssessments, dispatch]);

    const handleCloseDialog = useCallback(() => {
        setShowSaveAlert(false);

        // Resume the auto-save timer if there are unsaved changes
        if (AUTO_SAVE_ENABLED && (isChanged || changedAssessments.size > 0)) {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
            const currentAssessmentType = assessmentType;
            autoSaveTimerRef.current = setTimeout(() => {
                performAutoSave(currentAssessmentType);
            }, DEBOUNCE_DELAY);
        }
    }, [isChanged, changedAssessments, assessmentType, performAutoSave, AUTO_SAVE_ENABLED]);

    const handleMultiAssessmentConfirm = useCallback(
        async (assessmentActions: Record<AssessmentTypeEnum, 'complete' | 'agree' | 'not-reviewed' | null>) => {
            const assessmentTypes = [
                AssessmentTypeEnum.PastPresentHistory,
                AssessmentTypeEnum.PastSurgicalHistory,
                AssessmentTypeEnum.ReviewSystems,
                AssessmentTypeEnum.PhysicalExam,
            ];

            // Process each assessment: save changes first (if any), then apply the action
            for (const type of assessmentTypes) {
                const action = assessmentActions[type];
                const assessment = localAssessments[type];
                const hasChanges = changedAssessments.has(type);

                if (!assessment?.Assessment?.Categories?.[0]) {
                    continue; // Skip if assessment data is not available
                }

                try {
                    // Step 2: Process the user's selected action
                    if (action === 'complete') {
                        // Mark as complete (whether we just saved changes or not)
                        dispatch(setMemberAssessment(assessment));
                        await dispatch(saveAssessmentWorkflow(assessment.Assessment.Categories[0], true, false));
                    } else if (action === 'agree' && !hasChanges) {
                        // For 'agree', only process if there were no unsaved changes
                        dispatch(setMemberAssessment(assessment));
                        await dispatch(saveAssessmentWorkflow(assessment.Assessment.Categories[0], true, false));
                    }
                    // For 'not-reviewed', we don't need to do anything
                } catch (error) {
                    console.error(`Error saving ${ASSESSMENT_DISPLAY_NAME[type]}:`, error);
                    dispatch(setShowMessage(true, `Failed to save ${ASSESSMENT_DISPLAY_NAME[type]} assessment`, 'error'));
                    setShowSaveAlert(false);
                    return; // Stop processing further assessments
                }
            }

            // Clear all changed assessments after successful save
            setChangedAssessments(new Set());
            setModifiedAssessments(new Set());
            dispatch(setIsChanged(false));

            // After all assessments are processed, go to next menu
            dispatch(gotoNextMenu());
            setShowSaveAlert(false);
        },
        [dispatch, localAssessments, changedAssessments]
    );

    const handlePreviousClick = useCallback(() => {
        dispatch(gotoPreviousMenu());
    }, [dispatch]);

    // Pre-load all assessment data for performance
    useEffect(() => {
        dispatch(getPastPresentHistory(memberId));
        dispatch(getPastSurgicalHistory(memberId));
        dispatch(getReviewSystems(memberId));
        dispatch(getPhysicalExam(memberId));
    }, [dispatch, memberId]);

    useEffect(() => {
        dispatch(fetchTobaccoAndDrugAssessmentByMemberId(memberId));
    }, [dispatch, memberId]);

    useEffect(() => {
        if (isFetching && !isPageLoaded) {
            setIsPageLoaded(true);
        }
    }, [isFetching, isPageLoaded]);

    // Cleanup auto-save timer on unmount
    useEffect(() => {
        return () => {
            // Clear timeout on unmount
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, []);

    return (
        <Grid container direction="column" spacing={2} className="content-panel" position={'relative'}>
            <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 0 }}>
                <Typography variant="h6">Physical Health</Typography>
                <Tooltip title="Physical Health Help Document">
                    <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                </Tooltip>
            </Stack>
            {isFetching || !isPageLoaded || !localAssessments[assessmentType] ? (
                <Box display="flex" justifyContent="center" className="loader-container">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Header Section */}
                    <Grid size={12} sx={{ mb: 3 }} ref={headerRef}>
                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }} spacing={2}>
                            <Box>
                                <strong>Last Completed: </strong>
                                {Boolean(localAssessments[assessmentType]?.AssessmentDate) ? (
                                    <Chip size="small" label={formatDate(localAssessments[assessmentType].AssessmentDate, 'MM/dd/yyyy')} />
                                ) : (
                                    '-'
                                )}
                            </Box>

                            {/* Expand/Collapse Controls */}
                            <Stack direction="row" spacing={1}>
                                <Button size="small" variant="outlined" onClick={handleExpandAll}>
                                    Expand All
                                </Button>
                                <Button size="small" variant="outlined" onClick={handleCollapseAll}>
                                    Collapse All
                                </Button>
                            </Stack>
                        </Stack>
                    </Grid>

                    {/* Main Tabs */}
                    <Grid size={12} ref={tabsRef} sx={{ position: 'sticky', top: stickyTop, zIndex: 10, pt: 2, backgroundColor: '#fff' }}>
                        <Tabs
                            value={assessmentType}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            indicatorColor="primary"
                            TabIndicatorProps={{ sx: { height: 4 } }}
                            textColor="primary"
                        >
                            <Tab
                                icon={
                                    pastPresentHistoryMenu === assessmentStatusEnum.COMPLETED ? (
                                        <Tooltip title="Complete">
                                            <CheckCircleIcon fontSize="small" />
                                        </Tooltip>
                                    ) : undefined
                                }
                                iconPosition="end"
                                label={<Typography variant="caption">Past/Present History</Typography>}
                                value={AssessmentTypeEnum.PastPresentHistory}
                            />
                            <Tab
                                icon={
                                    pastSurgicalHistoryMenu === assessmentStatusEnum.COMPLETED ? (
                                        <Tooltip title="Complete">
                                            <CheckCircleIcon fontSize="small" />
                                        </Tooltip>
                                    ) : undefined
                                }
                                iconPosition="end"
                                label={<Typography variant="caption">Past Surgical History</Typography>}
                                value={AssessmentTypeEnum.PastSurgicalHistory}
                            />
                            <Tab
                                icon={
                                    reviewSystemsMenu === assessmentStatusEnum.COMPLETED ? (
                                        <Tooltip title="Complete">
                                            <CheckCircleIcon fontSize="small" />
                                        </Tooltip>
                                    ) : undefined
                                }
                                iconPosition="end"
                                label={<Typography variant="caption">Review Systems</Typography>}
                                value={AssessmentTypeEnum.ReviewSystems}
                            />
                            <Tab
                                icon={
                                    physicalExamMenu === assessmentStatusEnum.COMPLETED ? (
                                        <Tooltip title="Complete">
                                            <CheckCircleIcon fontSize="small" />
                                        </Tooltip>
                                    ) : undefined
                                }
                                iconPosition="end"
                                label={<Typography variant="caption">Physical Examination</Typography>}
                                value={AssessmentTypeEnum.PhysicalExam}
                            />
                        </Tabs>
                    </Grid>

                    {/* Categories Section */}
                    <Grid size={12}>
                        <Grid container spacing={2}>
                            <AssessmentCategoryList
                                memberId={memberId}
                                assessment={localAssessments[assessmentType]}
                                assessmentType={assessmentType}
                                expandedCategoryByTab={expandedCategoryByTab}
                                getCurrentMasterSelection={getCurrentMasterSelectionCached}
                                getMasterRadioOptions={getMasterRadioOptions}
                                handleCategoryToggle={handleCategoryToggle}
                                handleMasterRadioChange={handleMasterRadioChange}
                                handleOnChangeCategory={handleOnChangeCategory}
                                renderCategoryQuestions={renderCategoryQuestions}
                            />
                        </Grid>
                    </Grid>

                    {/* Footer Buttons */}
                    <Grid size={12} justifyContent="flex-end" className="content-footer">
                        <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
                            <Button disabled={isLoading} variant="contained" color="secondary" onClick={handlePreviousClick} startIcon={<ArrowBackIosIcon />}>
                                Previous
                            </Button>
                            <Button disabled={isLoading} variant="contained" onClick={handleOnClickSave} endIcon={<ArrowForwardIosIcon />}>
                                Next
                            </Button>
                        </Stack>
                    </Grid>
                </>
            )}

            {/* Multi-Assessment Dialog */}
            <MultiAssessmentDialog
                open={showSaveAlert}
                onClose={handleCloseDialog}
                onConfirm={handleMultiAssessmentConfirm}
                isLoading={isSaving}
                assessments={{
                    pastPresentHistory: {
                        hasChanges:
                            modifiedAssessments.has(AssessmentTypeEnum.PastPresentHistory) || changedAssessments.has(AssessmentTypeEnum.PastPresentHistory),
                        hasId: Boolean(engagementDraft.PastPresentHistoryId),
                        date: localAssessments[AssessmentTypeEnum.PastPresentHistory]?.CompletedDate || null,
                        isCompleted: pastPresentHistoryMenu === assessmentStatusEnum.COMPLETED || false,
                    },
                    pastSurgicalHistory: {
                        hasChanges:
                            modifiedAssessments.has(AssessmentTypeEnum.PastSurgicalHistory) || changedAssessments.has(AssessmentTypeEnum.PastSurgicalHistory),
                        hasId: Boolean(engagementDraft.PastSurgicalHistoryId),
                        date: localAssessments[AssessmentTypeEnum.PastSurgicalHistory]?.CompletedDate || null,
                        isCompleted: pastSurgicalHistoryMenu === assessmentStatusEnum.COMPLETED || false,
                    },
                    reviewSystems: {
                        hasChanges: modifiedAssessments.has(AssessmentTypeEnum.ReviewSystems) || changedAssessments.has(AssessmentTypeEnum.ReviewSystems),
                        hasId: Boolean(engagementDraft.ReviewSystemId),
                        date: localAssessments[AssessmentTypeEnum.ReviewSystems]?.CompletedDate || null,
                        isCompleted: reviewSystemsMenu === assessmentStatusEnum.COMPLETED || false,
                    },
                    physicalExam: {
                        hasChanges: modifiedAssessments.has(AssessmentTypeEnum.PhysicalExam) || changedAssessments.has(AssessmentTypeEnum.PhysicalExam),
                        hasId: Boolean(engagementDraft.PhysicalExamId),
                        date: localAssessments[AssessmentTypeEnum.PhysicalExam]?.CompletedDate || null,
                        isCompleted: physicalExamMenu === assessmentStatusEnum.COMPLETED || false,
                    },
                }}
            />
        </Grid>
    );
});

export default PhysicalHealth;
